import type { ProxyUploadOutcome } from './api';

type Status = ProxyUploadOutcome['status'];
type Identity = Omit<ProxyUploadOutcome, 'status' | 'uploadId'>;
/** Observation only: never creates demand, changes bytes, retries, or holds the ordinary publisher. */
export class ProxyUploadMonitor {
  private listeners = new Set<(result: ProxyUploadOutcome) => void>();
  private pending = new Set<() => void>();
  private nextId = 0;
  subscribe = (listener: (result: ProxyUploadOutcome) => void): (() => void) => {
    if (this.listeners.size >= 8) throw Error('Proxy upload observer limit');
    // Distinct subscriptions even if the caller reuses a callback.
    const wrapped = (result: ProxyUploadOutcome) => listener(result);
    this.listeners.add(wrapped);
    return () => { this.listeners.delete(wrapped); };
  };
  cancel(): void { for (const cancel of [...this.pending]) cancel(); }
  begin(identity: Identity, current: () => boolean, signals: AbortSignal[]) {
    const tuple = Object.freeze({ ...identity, uploadId: ++this.nextId });
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let resolve!: (value: ProxyUploadOutcome) => void;
    const result = new Promise<ProxyUploadOutcome>(done => { resolve = done; });
    const cancel = () => finish('cancelled');
    const finish = (status: Status): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      this.pending.delete(cancel);
      for (const signal of signals) signal.removeEventListener('abort', cancel);
      const outcome = Object.freeze({ ...tuple, status: status === 'uploaded' && !current() ? 'cancelled' as const : status });
      resolve(outcome);
      for (const listener of [...this.listeners]) {
        try { listener(outcome); } catch { /* Observation cannot interrupt media or another subscriber. */ }
      }
    };
    if (!current() || signals.some(signal => signal.aborted)) finish('cancelled');
    else if (!identity.broadcastId || !/^[a-f0-9]{64}$/.test(identity.sha256) || this.pending.size >= 64) finish('skipped');
    else {
      this.pending.add(cancel);
      for (const signal of signals) signal.addEventListener('abort', cancel, { once: true });
      // Includes queue residence, network upload and response; late success never replaces this outcome.
      timer = setTimeout(() => finish('timeout'), 5000);
    }
    return { finish, result };
  }
}
